// ================================================================================
// ECOMSIMPLY - PRODUCT RESEARCH PAGE COMPONENT
// Page principale de recherche de produits IA avec filtres et résultats
// ================================================================================

import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Brain, TrendingUp, AlertCircle, CheckCircle, Radar, Zap, Target } from 'lucide-react';
import ProductResearchFilters from './ProductResearchFilters';
import ProductResearchResults from './ProductResearchResults';
import ProductDetailDrawer from './ProductDetailDrawer';
import BlueOceanInterface from './BlueOceanInterface';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { useAuth } from '../hooks/useAuth';
import { useSupplierClick } from '../hooks/useSupplierClick';
import { useApiClient } from '../hooks/useApiClient';
import api, { apiPath } from '../lib/apiClient';

const ProductResearchPage = () => {
  // ================================================================================
  // HOOKS
  // ================================================================================
  const apiClient = useApiClient();
  
  // ================================================================================
  // STATE MANAGEMENT
  // ================================================================================
  
  // Onglet actif: "research" (standard) ou "blue-ocean" (Blue-Ocean Radar™)
  const [activeTab, setActiveTab] = useState('research');
  
  const [filters, setFilters] = useState({
    query: '',
    category: '',
    minPrice: '',
    maxPrice: '',
    minBSR: '',
    maxBSR: '',
    minRating: '',
    competition: '',
    marketplace: 'amazon.fr',
    budget: 10000,
    suppliers: 'verified',
    targetMargin: '',
    minROI: '',
    maxWeight: ''
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null); // ✅ NOUVEAU: Messages informatifs (non-erreurs)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDrawer, setShowProductDrawer] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  // Blue-Ocean specific state
  const [blueOceanOpportunities, setBlueOceanOpportunities] = useState([]);
  const [blueOceanLoading, setBlueOceanLoading] = useState(false);
  const [blueOceanError, setBlueOceanError] = useState(null);

  const handleSearch = useCallback(async () => {
    // NOUVELLE LOGIQUE: Recherche adaptative avec ou sans mots-clés
    const hasKeywords = filters.query && filters.query.trim();
    const hasMarginAndBudget = filters.targetMargin && filters.budget;
    
    if (!hasKeywords && !hasMarginAndBudget) {
      setError('Veuillez saisir soit des mots-clés, soit renseigner Marge cible min (%) et Budget pour lancer une recherche.');
      return;
    }

    setLoading(true);
    setError(null);
    setInfoMessage(null); // ✅ Reset message informatif
    setSearchPerformed(true);

    try {
      // Préparation de la requête pour l'IA Adaptative
      const searchRequest = {
        query: filters.query ? filters.query.trim() : '', // Peut être vide
        marketplace: filters.marketplace,
        constraints: {
          category: filters.category || undefined,
          min_price: filters.minPrice ? parseFloat(filters.minPrice) : undefined,
          max_price: filters.maxPrice ? parseFloat(filters.maxPrice) : undefined,
          min_bsr: filters.minBSR ? parseInt(filters.minBSR) : undefined,
          max_bsr: filters.maxBSR ? parseInt(filters.maxBSR) : undefined,
          min_rating: filters.minRating ? parseFloat(filters.minRating) : undefined,
          competition_level: filters.competition || undefined,
          target_margin_pct: filters.targetMargin ? parseFloat(filters.targetMargin) : undefined,
          min_roi_pct: filters.minROI ? parseFloat(filters.minROI) : undefined,
          weight_kg_max: filters.maxWeight ? parseFloat(filters.maxWeight) : undefined
        },
        budget_eur: parseInt(filters.budget),
        need_suppliers: true,
        
        // NOUVEAU: Mode de recherche adaptatif
        search_mode: hasKeywords ? 'keyword_based' : 'adaptive_ai',
        adaptive_context: {
          expert_qualification: 'Agent Adaptatif IA – Expert e-Commerce et Sourcing International',
          target_margin: filters.targetMargin ? parseFloat(filters.targetMargin) : undefined,
          budget: parseInt(filters.budget),
          strategy: 'exploratoire' // Stratégie IA pour recherche sans mots-clés
        }
      };

      console.log('🧠 IA Adaptative lancée:', searchRequest);

      const response = await api.post('/research/search', searchRequest);
      
      // ✅ GESTION RÉPONSE CORRIGÉE: Distinguer erreurs et messages informatifs
      if (response && response.products !== undefined) {
        setResults(response.products || []);
        
        // Si mode démo, afficher comme message informatif (pas erreur)
        if (response.demo_mode && response.message) {
          setInfoMessage(response.message);
          setError(null); // Pas d'erreur, juste informatif
        }
        
        console.log(`✅ ${response.products?.length || 0} produits trouvés par l'IA adaptative`);
      } else if (response.data && response.data.products !== undefined) {
        setResults(response.data.products || []);
        
        // Si mode démo, afficher comme message informatif (pas erreur)
        if (response.data.demo_mode && response.data.message) {
          setInfoMessage(response.data.message);
          setError(null); // Pas d'erreur, juste informatif
        }
        
        console.log(`✅ ${response.data.products?.length || 0} produits trouvés`);
      } else {
        throw new Error('Format de réponse inattendu');
      }
    } catch (err) {
      console.error('❌ Erreur de recherche IA:', err);
      
      // ✅ GESTION D'ERREUR CORRIGÉE: Distinguer vraies erreurs des messages démo
      if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else if (err.response?.status === 403) {
        setError('Accès non autorisé. Veuillez vérifier vos permissions.');
      } else if (err.response?.status === 429) {
        setError('Limite de requêtes atteinte. Veuillez patienter quelques minutes.');
      } else if (err.response?.status === 200 && err.response?.data?.demo_mode) {
        // ✅ NOUVEAU: Status 200 avec mode démo = message informatif, pas erreur
        setInfoMessage(err.response.data.message || 'Recherche en mode démo - fonctionnalité complète disponible avec API de production');
        setResults(err.response.data.products || []);
        setError(null); // Pas d'erreur réelle
      } else if (err.response?.data?.message) {
        // Message d'erreur spécifique du backend
        setError(err.response.data.message);
      } else if (err.response?.data?.demo_mode) {
        // Mode démo avec message informatif
        setInfoMessage(err.response.data.message || 'Recherche en mode démo - fonctionnalité complète disponible avec API de production');
        setError(null); // Pas d'erreur réelle
      } else {
        setError('Erreur de connexion. Vérifiez votre connexion internet.');
      }
      
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleProductClick = useCallback((product) => {
    setSelectedProduct(product);
    setShowProductDrawer(true);
  }, []);

  const handleSupplierClick = useCallback((product) => {
    console.log('🏭 Affichage des fournisseurs pour:', product.asin);
    // TODO: Implémenter l'affichage des fournisseurs
    alert(`Affichage des fournisseurs pour ${product.title}\n(Fonctionnalité en cours de développement)`);
  }, []);

  const handleViewSuppliers = useCallback((product) => {
    setShowProductDrawer(false);
    handleSupplierClick(product);
  }, [handleSupplierClick]);

  // ================================================================================
  // BLUE-OCEAN RADAR™ FUNCTIONS
  // ================================================================================
  
  const handleBlueOceanScan = useCallback(async () => {
    if (!filters.query || !filters.query.trim()) {
      setBlueOceanError('Veuillez saisir au moins un mot-clé pour lancer le scan Blue-Ocean.');
      return;
    }

    setBlueOceanLoading(true);
    setBlueOceanError(null);

    try {
      const scanRequest = {
        seeds: filters.query.split(' ').filter(word => word.length > 2), // Mots > 2 chars
        marketplace: filters.marketplace,
        max_items: 400,
        deep_analysis: true,
        include_suppliers: true
      };

      console.log('🌊 Blue-Ocean scan launched:', scanRequest);

      const response = await apiClient.post(apiPath('/research/blue-ocean/scan'), scanRequest);
      
      if (response && response.opportunities) {
        setBlueOceanOpportunities(response.opportunities);
        
        if (response.opportunities.length === 0) {
          setBlueOceanError('Aucune opportunité Blue-Ocean détectée avec ces paramètres. Essayez d\'autres mots-clés.');
        }
      } else {
        setBlueOceanError('Réponse inattendue du serveur.');
      }

    } catch (error) {
      console.error('Blue-Ocean scan error:', error);
      if (error.response?.status === 402) {
        setBlueOceanError('Quotas API atteints. Veuillez réessayer plus tard.');
      } else {
        setBlueOceanError('Erreur lors du scan Blue-Ocean. Vérifiez votre connexion.');
      }
      setBlueOceanOpportunities([]);
    } finally {
      setBlueOceanLoading(false);
    }
  }, [filters]);

  const loadTopBlueOceanOpportunities = useCallback(async () => {
    setBlueOceanLoading(true);
    setBlueOceanError(null);

    try {
      const response = await apiClient.get(apiPath('/research/blue-ocean/top'), { 
        params: { limit: 20, sort: 'bankability', min_bankability: 60 }
      });
      
      if (response && response.opportunities) {
        setBlueOceanOpportunities(response.opportunities);
      } else {
        setBlueOceanError('Aucune opportunité pré-calculée disponible.');
      }

    } catch (error) {
      console.error('Load top opportunities error:', error);
      setBlueOceanError('Erreur lors du chargement des opportunités.');
      setBlueOceanOpportunities([]);
    } finally {
      setBlueOceanLoading(false);
    }
  }, []);

  const handleBlueOceanProductClick = useCallback((opportunity) => {
    // Adapter format Blue-Ocean vers format standard pour le drawer
    const adaptedProduct = {
      asin: opportunity.asin,
      title: opportunity.title,
      price: opportunity.price_buybox,
      rating: opportunity.rating,
      reviews_count: opportunity.reviews_count,
      bsr: opportunity.bsr,
      bankability_score: opportunity.scores?.bankability || 0,
      trend_signal: opportunity.scores?.trend_signal || 'neutral',
      image_url: opportunity.images?.[0] || '',
      
      // Blue-Ocean specific data
      blue_ocean_scores: opportunity.scores,
      blueprint: opportunity.blueprint,
      supplier_brief: opportunity.supplier_brief,
      evidence: opportunity.evidence
    };
    
    setSelectedProduct(adaptedProduct);
    setShowProductDrawer(true);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header avec navigation par onglets */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Brain className="w-8 h-8 text-primary" />
            Recherche Produit IA
          </h1>
          <p className="text-gray-600 mt-2">
            Intelligence artificielle avancée pour l'identification d'opportunités e-commerce
          </p>
        </div>
        
        <div className="flex gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {activeTab === 'research' ? results.length : blueOceanOpportunities.length}
            </div>
            <div className="text-sm text-gray-500">
              {activeTab === 'research' ? 'Produits trouvés' : 'Opportunités détectées'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {activeTab === 'research' 
                ? results.filter(p => p.bankability_score >= 80).length
                : blueOceanOpportunities.filter(o => o.scores?.bankability >= 80).length
              }
            </div>
            <div className="text-sm text-gray-500">Excellents</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {activeTab === 'research'
                ? results.filter(p => p.competition_flag === 'very_low').length
                : blueOceanOpportunities.filter(o => o.scores?.ess >= 75).length
              }
            </div>
            <div className="text-sm text-gray-500">
              {activeTab === 'research' ? 'Faible concurrence' : 'Blue-Ocean'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {activeTab === 'research'
                ? results.filter(p => p.margin_percent >= (filters.targetMargin || 30)).length
                : blueOceanOpportunities.filter(o => o.scores?.bankability >= 70).length
              }
            </div>
            <div className="text-sm text-gray-500">
              {activeTab === 'research' ? 'Marge cible' : 'Prêts à exploiter'}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('research')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'research'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Recherche IA Standard
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('blue-ocean')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'blue-ocean'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Radar className="w-4 h-4" />
              Blue-Ocean Radar™
              <Badge className="bg-blue-100 text-blue-700 text-xs">
                Premium
              </Badge>
            </div>
          </button>
        </nav>
      </div>

      {/* Contenu conditionnel selon l'onglet actif */}
      {activeTab === 'research' ? (
        <>
          {/* Filtres de recherche standard */}
          <ProductResearchFilters
            filters={filters}
            onFiltersChange={setFilters}
            onSearch={handleSearch}
            loading={loading}
          />

          {/* Messages d'erreur/info */}
          {error && (
            <Card className="border-red-200 bg-red-50">
              <div className="flex items-center gap-3 text-red-800">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </Card>
          )}

          {infoMessage && (
            <Card className="border-blue-200 bg-blue-50">
              <div className="flex items-center gap-3 text-blue-800">
                <CheckCircle className="w-5 h-5" />
                <span>{infoMessage}</span>
                <div className="ml-auto flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700">
                    🌧️ Rainforest API
                  </Badge>
                  <Badge className="bg-green-100 text-green-700">
                    🧠 GPT-5 PRO
                  </Badge>
                  <Badge className="bg-orange-100 text-orange-700">
                    🏭 Sourcing International
                  </Badge>
                </div>
              </div>
            </Card>
          )}

          {/* Résultats de recherche standard */}
          {searchPerformed && (
            <ProductResearchResults
              results={results}
              loading={loading}
              onProductClick={handleProductClick}
              onSupplierClick={handleSupplierClick}
            />
          )}
        </>
      ) : (
        <>
          {/* Blue-Ocean Radar™ Interface */}
          <BlueOceanInterface
            filters={filters}
            onFiltersChange={setFilters}
            opportunities={blueOceanOpportunities}
            loading={blueOceanLoading}
            error={blueOceanError}
            onScan={handleBlueOceanScan}
            onLoadTop={loadTopBlueOceanOpportunities}
            onOpportunityClick={handleBlueOceanProductClick}
          />
        </>
      )}

      {/* Drawer de détails produit */}
      <ProductDetailDrawer
        product={selectedProduct}
        isOpen={showProductDrawer}
        onClose={() => setShowProductDrawer(false)}
        onViewSuppliers={handleViewSuppliers}
      />
    </div>
  );
};

export default ProductResearchPage;